import { useState, useEffect } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import type { LucideIcon } from 'lucide-react'
import {
  LayoutDashboard,
  Users,
  CreditCard,
  ChefHat,
  Settings,
  Menu,
  BarChart3,
  UserCog,
  CalendarCheck,
  LayoutGrid,
  ChevronLeft,
  ChevronRight,
  Warehouse,
  Receipt,
  Radio,
  FileWarning,
  Store,
  Lock,
  Contact,
  BadgePercent,
  Megaphone,
  Inbox,
  MapPin,
  QrCode,
  Zap,
  Gift,
  Star,
  MessageSquare,
  LifeBuoy,
  Award,
  UserPlus,
  Filter,
  Image as PosterIcon,
  Globe,
  ClipboardList,
  CalendarDays,
  ShoppingBag,
  UtensilsCrossed,
  CookingPot,
  PieChart,
  ScanLine,
} from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { UserMenu } from '@/components/ui/user-menu'
import type { User as UserType } from '@/types'
import apiClient from '@/api/client'
import { useKitchenSettings, isKDSEnabled } from '@/hooks/useKitchenSettings'
import { useOnlineOrderingEnabled } from '@/hooks/useOnlineOrderingEnabled'
import { useEdition } from '@/hooks/useEdition'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { useReceiptScanEnabled } from '@/hooks/useReceiptScanEnabled'
import { FD } from '@/lib/inventoryFlow/flowDashboardStrings'
import { Workflow, Bike } from 'lucide-react'
import { useRiderAppEnabled } from '@/hooks/useRiderAppEnabled'
import { useHRPayrollEnabled } from '@/hooks/useHRPayrollEnabled'
import {
  canAccessAdminRoute,
  marketingLandingPath,
  navSectionIdsForRole,
} from '@/lib/staff-roles'

// Sections that only exist in the full POS edition — hard-hidden on
// marketing-edition deploys (BHOOKLY_EDITION=marketing). Visible there:
// staff, customers, marketing, settings. Keep in step with
// POS_ONLY_ADMIN_PATH_PREFIXES in lib/staff-roles.ts.
const POS_ONLY_SECTION_IDS = new Set([
  'dashboard',
  'counter',
  'server',
  'online-orders',
  'dispatch',
  'rider',
  'website',
  'delivery-zones',
  'day-close',
  'inventory',
  'production',
  'kitchen-flow',
  'catering',
  'menu',
  'tables',
  'kitchen',
  'stations',
  'void-log',
  'expenses',
  'receipts',
  'reports',
  'hr',
])
import { cn } from '@/lib/utils'
import { REPUTATION_INBOX_ENABLED } from '@/lib/reputationInbox'
import {
  POS_NAV_LAYOUT,
  activeGroupId,
  buildNavTree,
  strongestSignal,
  type NavNode,
  type NavSignal,
} from '@/lib/adminNavGroups'

export const MOBILE_ADMIN_HEADER_HEIGHT_CLASS = 'h-14'

/** Collapsed desktop rail — base 4.625rem + 10% for logo + centered icons */
const COLLAPSED_RAIL_WIDTH_CLASS = 'w-[calc(4.625rem*1.1)]'

type AdminSectionDef = {
  id: string
  label: string
  /**
   * The row label in the grouped sidebar, where the group already says what
   * kind of thing it is ("View Reports" under Reports & expenses is just
   * "Reports"). `label` stays the page title (adminNavTitleFromPath).
   */
  short?: string
  icon: LucideIcon
  description: string
  href: string
}

const adminSections: AdminSectionDef[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    description: 'Overview and statistics',
    href: '/admin/dashboard',
  },
  {
    id: 'counter',
    label: 'Point of Sale (POS)',
    short: 'POS',
    icon: CreditCard,
    description: 'Payment processing',
    href: '/admin/counter',
  },
  {
    id: 'server',
    label: 'Server Interface',
    short: 'Server',
    icon: Users,
    description: 'Server order interface',
    href: '/admin/server',
  },
  {
    id: 'online-orders',
    label: 'Online Orders',
    short: 'Online orders',
    icon: Inbox,
    description: 'Incoming online orders to accept or reject',
    href: '/admin/online-orders',
  },
  {
    id: 'dispatch',
    label: 'Rider Management',
    icon: Bike,
    description: 'Assign riders, follow deliveries, take rider cash',
    href: '/admin/dispatch',
  },
  {
    id: 'rider',
    label: 'My Deliveries',
    short: 'My deliveries',
    icon: Bike,
    description: 'Your delivery queue',
    href: '/admin/rider',
  },
  {
    id: 'website',
    label: 'Website',
    icon: Globe,
    description: 'Your ordering website: theme, colours, photos, hours',
    href: '/admin/website',
  },
  {
    id: 'delivery-zones',
    label: 'Delivery Zones',
    short: 'Delivery zones',
    icon: MapPin,
    description: 'Delivery areas, charges, and minimums for online orders',
    href: '/admin/delivery-zones',
  },
  {
    id: 'day-close',
    label: 'Daily Closing',
    short: 'Daily closing',
    icon: Lock,
    description: 'Start the day, count cash, finish the day — every day',
    href: '/admin/day-close',
  },
  {
    id: 'inventory',
    label: 'Store Inventory',
    short: 'Inventory',
    icon: Warehouse,
    description: 'Supplies & stock management',
    href: '/admin/inventory',
  },
  {
    id: 'menu',
    label: 'Manage Menu',
    short: 'Menu',
    icon: Menu,
    description: 'Categories and products',
    href: '/admin/menu',
  },
  {
    id: 'tables',
    label: 'Manage Tables',
    short: 'Tables',
    icon: LayoutGrid,
    description: 'Dining table management',
    href: '/admin/tables',
  },
  {
    id: 'staff',
    label: 'Manage Staff',
    short: 'Staff',
    icon: UserCog,
    description: 'User and role management',
    href: '/admin/staff',
  },
  {
    id: 'hr',
    label: 'Staff & Payroll',
    short: 'Payroll & attendance',
    icon: CalendarCheck,
    description: 'Attendance, leave, advances and salaries',
    href: '/admin/hr',
  },
  {
    id: 'customers',
    label: 'Customers',
    icon: Contact,
    description: 'Customer directory, profiles, and merge',
    href: '/admin/customers',
  },
  // Bank card promos live inside the Marketing Suite as a tab — see
  // marketing/AdminCardPromos.tsx. Managers get this section for that one
  // tab; label/href below are rewritten for them so it doesn't read as
  // access to the whole suite.
  {
    id: 'marketing',
    label: 'Marketing Suite',
    short: 'Marketing suite',
    icon: Megaphone,
    description: 'Segments, promo codes, loyalty, campaigns, QR codes, and bank card promos',
    href: '/admin/marketing/overview',
  },
  {
    id: 'reports',
    label: 'View Reports',
    short: 'Reports',
    icon: BarChart3,
    description: 'Analytics and reports',
    href: '/admin/reports',
  },
  {
    id: 'expenses',
    label: 'View Expenses',
    short: 'Expenses',
    icon: Receipt,
    description: 'Expense tracking and P&L',
    href: '/admin/expenses',
  },
  // F-FE-N192: the receipts inbox — hidden unless FEATURE_RECEIPT_SCAN is on.
  {
    id: 'receipts',
    label: 'Receipts',
    short: 'Receipts',
    icon: ScanLine,
    description: 'Scan receipts and review what was read',
    href: '/admin/receipts',
  },
  {
    id: 'void-log',
    label: 'Void Log',
    short: 'Void log',
    icon: FileWarning,
    description: 'Voided items audit trail',
    href: '/admin/void-log',
  },
  {
    id: 'kitchen',
    label: 'Kitchen Display',
    short: 'Kitchen display',
    icon: ChefHat,
    description: 'Kitchen order display',
    href: '/admin/kitchen',
  },
  // Kitchen flow (lane C) — hidden unless kitchen_flow is on.
  {
    id: 'production',
    label: 'Kitchen Production',
    short: 'Production',
    icon: ClipboardList,
    description: 'Ask the store for stock, receive it, send returns',
    href: '/admin/production',
  },
  // Kitchen flow Stage 3 — the owner's flow dashboard (admin; hidden unless kitchen_flow is on).
  {
    id: 'kitchen-flow',
    label: FD.nav,
    icon: Workflow,
    description: FD.navDescription,
    href: '/admin/kitchen-flow',
  },
  // Kitchen flow Stage 2B — catering bookings, hidden unless kitchen_flow is on.
  {
    id: 'catering',
    label: 'Catering',
    icon: CalendarDays,
    description: 'Event bookings, payments and invoices',
    href: '/admin/catering',
  },
  {
    id: 'stations',
    label: 'Kitchen Stations',
    short: 'Kitchen stations',
    icon: Radio,
    description: 'KOT routing configuration',
    href: '/admin/stations',
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    description: 'System configuration',
    href: '/admin/settings',
  },
]

const sectionById = (id: string): AdminSectionDef =>
  adminSections.find((s) => s.id === id)!

type MarketingNavGroup = { header: string | null; items: AdminSectionDef[] }

// Marketing-edition sidebar (BHOOKLY_EDITION=marketing): with no POS chrome
// competing for the rail, the Marketing Suite's tabs become first-class nav
// items under small group headers instead of one crammed tab bar. Hrefs
// mirror MarketingShell's TABS. Bank card promos are deliberately absent —
// a POS-checkout feature (see POS_ONLY_ADMIN_PATH_PREFIXES).
const MARKETING_EDITION_NAV_GROUPS: MarketingNavGroup[] = [
  {
    header: null,
    items: [
      {
        id: 'mkt-overview',
        label: 'Overview',
        icon: LayoutDashboard,
        description: 'Marketing KPIs, engagement, and live activity',
        href: '/admin/marketing/overview',
      },
    ],
  },
  {
    header: 'Grow',
    items: [
      {
        id: 'mkt-promo-codes',
        label: 'Promo codes',
        icon: BadgePercent,
        description: 'Discount codes customers redeem',
        href: '/admin/marketing/promo-codes',
      },
      {
        id: 'mkt-campaigns',
        label: 'Campaigns',
        icon: Megaphone,
        description: 'One-off sends to a customer segment',
        href: '/admin/marketing/campaigns',
      },
      {
        id: 'mkt-automations',
        label: 'Automations',
        icon: Zap,
        description: 'Always-on triggered messages',
        href: '/admin/marketing/automations',
      },
      {
        id: 'mkt-qr-codes',
        label: 'QR codes',
        icon: QrCode,
        description: 'Trackable QR codes for tables, flyers, and socials',
        href: '/admin/marketing/qr-codes',
      },
      {
        id: 'mkt-posters',
        label: 'Posters',
        icon: PosterIcon,
        description: 'Print-ready posters for every QR you publish',
        href: '/admin/marketing/posters',
      },
    ],
  },
  {
    header: 'Loyalty',
    items: [
      {
        id: 'mkt-loyalty',
        label: 'Loyalty program',
        icon: Award,
        description: 'Punch-card rules and rewards',
        href: '/admin/marketing/loyalty',
      },
      {
        id: 'mkt-enrolment',
        label: 'Enrolment',
        icon: UserPlus,
        description: 'Wallet-card signup link, QR, and placards',
        href: '/admin/marketing/enrolment',
      },
      {
        id: 'mkt-members',
        label: 'Members',
        icon: Users,
        description: 'Loyalty members and their balances',
        href: '/admin/marketing/members',
      },
      {
        id: 'mkt-gift-cards',
        label: 'Gift cards',
        icon: Gift,
        description: 'Issue and track gift cards',
        href: '/admin/marketing/gift-cards',
      },
    ],
  },
  {
    header: 'Reputation',
    items: [
      {
        id: 'mkt-reviews',
        label: 'Reviews',
        icon: Star,
        description: 'Review capture, gating, and alert routing',
        href: '/admin/marketing/reviews',
      },
      // Hidden while Google Business Profile API access is pending
      // (lib/reputationInbox.ts).
      ...(REPUTATION_INBOX_ENABLED
        ? [
            {
              id: 'mkt-reputation',
              label: 'Reputation',
              icon: MessageSquare,
              description: 'Public Google reviews inbox',
              href: '/admin/marketing/reputation',
            },
          ]
        : []),
      {
        id: 'mkt-recovery',
        label: 'Guest recovery',
        icon: LifeBuoy,
        description: 'Win back unhappy guests before they post',
        href: '/admin/marketing/recovery',
      },
    ],
  },
  {
    header: 'Audience',
    items: [
      sectionById('customers'),
      {
        id: 'mkt-segments',
        label: 'Segments',
        icon: Filter,
        description: 'Saved customer filters for campaigns',
        href: '/admin/marketing/segments',
      },
    ],
  },
  {
    header: 'Workspace',
    items: [sectionById('staff'), sectionById('settings')],
  },
]

/**
 * The Day Close state as a dot (F-FE-N187). It used to be a word pill
 * (LIVE / STAGED / CLOSED) on the row — the loudest thing in the panel on
 * every screen. A dot keeps the signal at a glance; the word moves into the
 * row's tooltip and screen-reader text. The row stands on its own in the nav
 * (not inside Sell), so "no day open" is never folded out of sight.
 */
function dayCloseSignal(
  state: 'open' | 'staged' | 'closed' | 'reopened' | undefined,
): { signal: NavSignal; text: string } {
  // No day open — the most urgent state.
  if (!state) return { signal: 'danger', text: 'No day open' }
  if (state === 'staged') return { signal: 'warn', text: 'Close staged' }
  // Sealed, then a sale arrived: the owner should run End-the-day again.
  if (state === 'reopened') return { signal: 'warn', text: 'Reopened' }
  if (state === 'closed') return { signal: 'muted', text: 'Day sealed' }
  return { signal: 'ok', text: 'Day open' }
}

const SIGNAL_DOT: Record<NavSignal, string> = {
  danger: 'bg-rose-500',
  warn: 'bg-amber-500',
  ok: 'bg-emerald-500',
  muted: 'bg-muted-foreground/40',
}

function SignalDot({ signal, className }: { signal: NavSignal; className?: string }) {
  return <span aria-hidden className={cn('size-2 shrink-0 rounded-full', SIGNAL_DOT[signal], className)} />
}

/** Group icons — the children keep their own. */
const GROUP_ICONS: Record<string, LucideIcon> = {
  sell: ShoppingBag,
  online: Globe,
  'menu-floor': UtensilsCrossed,
  kitchen: CookingPot,
  'marketing-group': Megaphone,
  'reports-expenses': PieChart,
}

const OPEN_GROUPS_KEY = 'bhookly.sidebar.openGroups'

function readOpenGroups(): Set<string> {
  try {
    const raw = window.localStorage.getItem(OPEN_GROUPS_KEY)
    const ids = raw ? (JSON.parse(raw) as unknown) : []
    return new Set(Array.isArray(ids) ? ids.filter((x): x is string => typeof x === 'string') : [])
  } catch {
    return new Set()
  }
}

function writeOpenGroups(ids: Set<string>) {
  try {
    window.localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify([...ids]))
  } catch {
    /* private window / blocked storage: groups just start closed next time */
  }
}

// Marketing tools included so /admin/marketing/* sub-pages resolve to their
// own label (previously anything but /overview fell through to 'POS Admin').
// adminSections comes first, so on ties (e.g. the overview href) the full-POS
// 'Marketing Suite' label wins.
const NAV_TITLE_SOURCES: { label: string; href: string }[] = [
  ...adminSections,
  ...MARKETING_EDITION_NAV_GROUPS.flatMap((g) => g.items),
]

/** Longest-prefix match for sidebar label; used by mobile shell title. */
export function adminNavTitleFromPath(pathname: string): string {
  let best = 'POS Admin'
  let bestLen = -1
  for (const s of NAV_TITLE_SOURCES) {
    if (pathname === s.href || pathname.startsWith(`${s.href}/`)) {
      if (s.href.length > bestLen) {
        bestLen = s.href.length
        best = s.label
      }
    }
  }
  return best
}

interface AdminSidebarProps {
  user: UserType
  /** Viewport &lt; lg — parent drives drawer open state */
  isNarrowViewport: boolean
  narrowDrawerOpen: boolean
  onNarrowDrawerOpenChange: (open: boolean) => void
}

export function AdminSidebar({
  user,
  isNarrowViewport,
  narrowDrawerOpen,
  onNarrowDrawerOpenChange,
}: AdminSidebarProps) {
  // Tablets (768-1023) start with the icon rail so content keeps its width;
  // desktop starts expanded. User toggle still wins after mount.
  const [desktopCollapsed, setDesktopCollapsed] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 768px) and (max-width: 1023px)').matches
  )
  const [, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)
  const location = useLocation()

  const { data: settingsRes } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 1000 * 60 * 5,
  })
  const kitchen = useKitchenSettings()
  const kdsOn = isKDSEnabled(kitchen.mode)
  // Server (table-service) interface is hidden when a restaurant turns it off
  // under Settings → Order Types. Absent/true → shown; explicit false → hidden.
  const serverInterfaceOn =
    ((settingsRes?.data as Record<string, unknown>)?.['ui.server_interface_enabled']) !== false

  // Product edition — hides every POS section on marketing-edition deploys
  // (hard hide, same treatment as kdsOn: those tenants didn't buy a till, so
  // there is nothing to upsell from the nav).
  const { posEnabled } = useEdition()
  const kitchenFlow = useKitchenFlowEnabled()
  const riderApp = useRiderAppEnabled()
  const receiptScan = useReceiptScanEnabled()
  const hrPayroll = useHRPayrollEnabled()

  // Live pill on the Day Close item — keeps managers aware of "no day
  // open" or "close staged" states even when they're on another screen.
  // Edition-gated: on marketing deploys the endpoint 403s (pos_not_enabled).
  const showDayClosePill = posEnabled && (user.role === 'admin' || user.role === 'manager')
  const { data: dayOpsCurrent } = useQuery({
    queryKey: ['day-ops', 'current'],
    queryFn: () => apiClient.getDayOpsCurrent(),
    enabled: showDayClosePill,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
  const dayOpsState = dayOpsCurrent?.data?.status as
    | 'open'
    | 'staged'
    | 'closed'
    | 'reopened'
    | undefined

  const logoUrl =
    ((settingsRes?.data as Record<string, unknown>)?.receipt_logo_url as string) ||
    ''

  const allowedIds = navSectionIdsForRole(user.role)
  const visibleAdminSections = adminSections
    .filter((s) => {
      if (s.id === 'kitchen' && !kdsOn) return false
      if (s.id === 'server' && !serverInterfaceOn) return false
      // Admin/manager configure stations under Settings → Kitchen; kitchen role has no Settings access and uses /admin/stations.
      if (s.id === 'stations' && user.role !== 'kitchen' && user.role !== 'kitchen_manager') return false
      if ((s.id === 'production' || s.id === 'catering') && !kitchenFlow) return false
      if (s.id === 'receipts' && !receiptScan.enabled) return false
      if (s.id === 'kitchen-flow' && (!kitchenFlow || user.role !== 'admin')) return false
      if (s.id === 'dispatch' && !riderApp) return false
      if (s.id === 'hr' && !hrPayroll) return false
      // The rider's own queue is a rider's screen; admins dispatch instead.
      if (s.id === 'rider' && (!riderApp || user.role !== 'rider')) return false
      if (!posEnabled && POS_ONLY_SECTION_IDS.has(s.id)) return false
      if (allowedIds !== null && !allowedIds.has(s.id)) return false
      return true
    })
    // A manager's only marketing surface is the Bank card promos tab, so the
    // nav item names that instead of the suite it can't otherwise open.
    .map((s) =>
      s.id === 'marketing' && user.role === 'manager'
        ? {
            ...s,
            label: 'Bank Card Promos',
            short: 'Bank card promos',
            icon: BadgePercent,
            description: 'Card-payment discounts reimbursed by banks (HBL, Meezan, …)',
            href: marketingLandingPath(user.role),
          }
        : s,
    )

  // Marketing edition: the suite's tools are the nav. Group headers are
  // interleaved into the flat entry list the renderer maps over; per-item
  // role filtering reuses canAccessAdminRoute (same rule MarketingShell
  // applies to its tabs), and a group whose tools all filter out vanishes.
  const navEntries: (AdminSectionDef | { groupHeader: string })[] = posEnabled
    ? visibleAdminSections
    : MARKETING_EDITION_NAV_GROUPS.map((g) => ({
        ...g,
        items:
          user.role === 'admin'
            ? g.items
            : g.items.filter((i) => canAccessAdminRoute(user.role, i.href)),
      }))
        .filter((g) => g.items.length > 0)
        .flatMap((g) => [
          ...(g.header ? [{ groupHeader: g.header }] : []),
          ...g.items,
        ])

  // Live count of pending online orders for the "Online Orders" nav badge.
  // Only polled for roles that can see the section (avoids 401 noise) AND on
  // tenants with the premium add-on (avoids 403 noise — locked tenants show a
  // golden lock instead; the nav item stays visible as the upsell surface).
  const canSeeOnlineOrders = allowedIds === null || allowedIds.has('online-orders')
  const { enabled: onlineOrderingOn, isLoading: onlineOrderingLoading } =
    useOnlineOrderingEnabled()
  const { data: pendingOnlineOrders } = useQuery({
    queryKey: ['online-orders', 'pending'],
    queryFn: () => apiClient.getOnlineOrders().then((r) => r.data ?? []),
    enabled: posEnabled && canSeeOnlineOrders && onlineOrderingOn,
    refetchInterval: 15_000,
  })
  const onlineOrdersCount = pendingOnlineOrders?.length ?? 0
  // Receipts waiting on this person (F-FE-N192): only where the section shows.
  const canSeeReceipts = posEnabled && receiptScan.enabled && (allowedIds === null || allowedIds.has('receipts'))
  const { data: receiptSummary } = useQuery({
    queryKey: ['receiptScanSummary'],
    queryFn: () => apiClient.getReceiptScanSummary().then(r => r.data),
    enabled: canSeeReceipts,
    refetchInterval: 60_000,
  })
  const receiptsWaiting = receiptSummary?.waiting ?? 0
  // Nothing renders while the gate resolves — no lock/badge flash either way.
  const showOnlineOrdersLock = !onlineOrderingOn && !onlineOrderingLoading

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth
      setIsMobile(width < 768)
      setIsTablet(width >= 768 && width < 1024)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  const isActiveRoute = (href: string) => {
    if (location.pathname === href) return true
    // Marketing edition lists every marketing tool as its own nav item, so
    // each highlights only for its own subtree — the full-POS blanket rule
    // below would light up all of them at once.
    if (!posEnabled) return location.pathname.startsWith(`${href}/`)
    // Marketing has sub-routes (/admin/marketing/promo-codes,
    // /admin/marketing/loyalty, …) and we want the parent nav item to
    // stay highlighted across them. Generalise: any section href under
    // /admin/marketing/ counts as a marketing route.
    if (href.startsWith('/admin/marketing/') && location.pathname.startsWith('/admin/marketing/')) {
      return true
    }
    return false
  }

  const showNarrowDrawer = isNarrowViewport && narrowDrawerOpen
  const sidebarFullContent = isNarrowViewport ? showNarrowDrawer : !desktopCollapsed

  const closeNarrowDrawer = () => onNarrowDrawerOpenChange(false)

  const toggleDesktop = () => setDesktopCollapsed((c) => !c)

  const desktopRailCollapsed = !isNarrowViewport && desktopCollapsed

  const sidebarExpanded =
    isNarrowViewport ? showNarrowDrawer : !desktopCollapsed

  const toggleAriaLabel = isNarrowViewport
    ? 'Close navigation menu'
    : desktopCollapsed
      ? 'Expand sidebar'
      : 'Collapse sidebar'

  const ToggleIcon = isNarrowViewport || !desktopCollapsed ? ChevronLeft : ChevronRight

  const logoImgClass = cn(
    'object-contain',
    desktopRailCollapsed
      ? 'w-full max-h-[4.75rem] px-1.5 py-2'
      // Cap the height so a square/tall logo doesn't eat the sidebar — the old
      // `w-full h-auto` rendered a square logo ~256px tall and swallowed most
      // of the panel in landscape. max-h is in rem so it tracks the UI scale.
      : 'mx-auto h-auto max-h-16 w-auto'
  )

  /*
   * Tenant-logo fallback plate. `bg-primary` alone is the shadcn inverted
   * primary — near-black in light, near-WHITE in dark — so on the dark sidebar
   * this rendered as a glaring white slab, the loudest thing on the screen
   * (F-FE-N55). The tokens were individually correct; the combination was not,
   * which is why no class-level sweep caught it. Dark gets the brand ember
   * instead, so the identity slot reads as a deliberate mark rather than a
   * hole punched in the rail. Light is unchanged.
   */
  const fallbackStoreClass = cn(
    'flex w-full items-center justify-center bg-primary dark:bg-bhk-ember',
    desktopRailCollapsed ? 'h-14' : 'h-12'
  )

  const fallbackStoreIconClass = cn(
    'text-primary-foreground',
    desktopRailCollapsed ? 'size-8' : 'size-6'
  )

  /** Desktop: toggle below logo (saves horizontal space); drawer: logo + close in one row */
  const stackToggleBelowBrand = !isNarrowViewport


  // ---- Grouped nav (F-FE-N187) -------------------------------------------
  // Full POS only; the marketing edition keeps its own static groups.
  const navTree: NavNode<AdminSectionDef>[] = posEnabled
    ? buildNavTree(visibleAdminSections, POS_NAV_LAYOUT)
    : []
  const currentGroupId = activeGroupId(navTree, isActiveRoute)

  // Groups the operator opened, remembered per device.
  const [openGroups, setOpenGroups] = useState<Set<string>>(() =>
    typeof window === 'undefined' ? new Set() : readOpenGroups(),
  )
  // The group holding the current page is open unless the operator folds it;
  // navigating to another group's page resets that.
  const [foldedCurrent, setFoldedCurrent] = useState(false)
  useEffect(() => setFoldedCurrent(false), [currentGroupId])

  const groupOpen = (id: string) => (id === currentGroupId ? !foldedCurrent : openGroups.has(id))
  const toggleGroup = (id: string) => {
    if (id === currentGroupId) {
      setFoldedCurrent((f) => !f)
      return
    }
    setOpenGroups((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      writeOpenGroups(next)
      return next
    })
  }

  // What each row carries on its right.
  const daySignal = showDayClosePill ? dayCloseSignal(dayOpsState) : null
  const signalFor = (id: string): NavSignal | undefined =>
    id === 'day-close' ? daySignal?.signal : undefined
  const countFor = (id: string): number =>
    id === 'online-orders' && !showOnlineOrdersLock ? onlineOrdersCount : id === 'receipts' ? receiptsWaiting : 0
  const lockedFor = (id: string): boolean =>
    showOnlineOrdersLock && (id === 'online-orders' || id === 'delivery-zones' || id === 'website')
  const hintFor = (s: AdminSectionDef): string =>
    s.id === 'day-close' && daySignal
      ? `${s.label} · ${daySignal.text}`
      : lockedFor(s.id)
        ? `${s.label} · premium add-on`
        : countFor(s.id) > 0
          ? `${s.label} · ${countFor(s.id)} waiting`
          : s.label

  // ---- Row renderers (F-FE-N187) -----------------------------------------
  // One loud thing at a time: only the current top-level page is a filled
  // pill; a current child page is a soft fill inside its group.
  const rowSize = isTablet ? 'h-10 text-[15px]' : 'h-9 text-sm'
  const childSize = isTablet ? 'h-9 text-sm' : 'h-8 text-[13px]'
  const rowBase =
    'flex w-full touch-manipulation items-center rounded-lg outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring'
  const [flyout, setFlyout] = useState<string | null>(null)
  const onNavigate = () => {
    setFlyout(null)
    if (isNarrowViewport) closeNarrowDrawer()
  }

  const countPill = (n: number) => (
    <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-red-600 px-1.5 text-[11px] font-semibold leading-none tabular-nums text-white">
      {n > 99 ? '99+' : n}
    </span>
  )

  const trailing = (id: string) => {
    if (lockedFor(id)) {
      return <Lock className="size-3.5 shrink-0 text-amber-500" aria-label="Premium add-on" />
    }
    const n = countFor(id)
    if (n > 0) return countPill(n)
    const sig = signalFor(id)
    return sig ? <SignalDot signal={sig} /> : null
  }

  // Screen-reader text for what the dot / count / lock says visually.
  const srStatus = (s: AdminSectionDef) => {
    const hint = hintFor(s)
    return hint !== s.label ? <span className="sr-only">{hint.slice(s.label.length)}</span> : null
  }

  const topRow = (s: AdminSectionDef) => {
    const active = isActiveRoute(s.href)
    const Icon = s.icon
    return (
      <Link
        key={s.id}
        to={s.href}
        onClick={onNavigate}
        title={hintFor(s) !== s.label ? hintFor(s) : undefined}
        aria-current={active ? 'page' : undefined}
        className={cn(
          rowBase,
          rowSize,
          'gap-3 px-3 font-medium',
          active ? 'bg-primary text-primary-foreground' : 'text-foreground/80 hover:bg-muted hover:text-foreground',
        )}
      >
        <Icon className="size-[18px] shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{s.short ?? s.label}</span>
        {trailing(s.id)}
        {srStatus(s)}
      </Link>
    )
  }

  const childRow = (s: AdminSectionDef) => {
    const active = isActiveRoute(s.href)
    const Icon = s.icon
    return (
      <Link
        key={s.id}
        to={s.href}
        onClick={onNavigate}
        title={hintFor(s) !== s.label ? hintFor(s) : undefined}
        aria-current={active ? 'page' : undefined}
        className={cn(
          rowBase,
          childSize,
          'gap-2.5 rounded-md pl-2.5 pr-2',
          active
            ? 'bg-muted font-medium text-foreground'
            : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        )}
      >
        <Icon className="size-4 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{s.short ?? s.label}</span>
        {trailing(s.id)}
        {srStatus(s)}
      </Link>
    )
  }

  type GroupNode = Extract<NavNode<AdminSectionDef>, { kind: 'group' }>

  // While a group is folded, its most urgent child signal rides on it.
  const groupBadge = (g: GroupNode) => {
    // Every child behind the add-on (Online, on a tenant without it): say so
    // on the folded group rather than only inside it.
    if (g.items.every((i) => lockedFor(i.id))) {
      return <Lock className="size-3.5 shrink-0 text-amber-500" aria-label="Premium add-on" />
    }
    const n = g.items.reduce((sum, i) => sum + countFor(i.id), 0)
    if (n > 0) return countPill(n)
    const sig = strongestSignal(g.items.map((i) => signalFor(i.id)))
    return sig ? <SignalDot signal={sig} /> : null
  }

  const groupBlock = (g: GroupNode) => {
    const open = groupOpen(g.id)
    const Icon = GROUP_ICONS[g.id] ?? LayoutGrid
    const holdsCurrent = g.id === currentGroupId
    const panelId = `nav-group-${g.id}`
    return (
      <div key={g.id}>
        <button
          type="button"
          onClick={() => toggleGroup(g.id)}
          aria-expanded={open}
          aria-controls={panelId}
          className={cn(
            rowBase,
            rowSize,
            'gap-3 px-3 font-medium',
            holdsCurrent && !open
              ? 'bg-muted text-foreground'
              : 'text-foreground/80 hover:bg-muted hover:text-foreground',
          )}
        >
          <Icon className="size-[18px] shrink-0" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-left">{g.label}</span>
          {!open && groupBadge(g)}
          <ChevronRight
            className={cn(
              'size-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none',
              open && 'rotate-90',
            )}
            aria-hidden
          />
        </button>
        {open && (
          <div id={panelId} className="mb-1 ml-[1.3rem] mt-0.5 space-y-0.5 border-l border-border pl-2">
            {g.items.map(childRow)}
          </div>
        )}
      </div>
    )
  }

  // ---- Collapsed icon rail -------------------------------------------------
  const railBadge = (count: number, signal: NavSignal | undefined, locked: boolean) =>
    locked ? (
      <Lock
        className="absolute -right-1 -top-1 size-3.5 rounded-full bg-card p-0.5 text-amber-500"
        aria-label="Premium add-on"
      />
    ) : count > 0 ? (
      <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-bold leading-none text-white motion-safe:animate-pulse">
        {count > 9 ? '9+' : count}
      </span>
    ) : signal ? (
      <SignalDot signal={signal} className="absolute -right-0.5 -top-0.5 ring-2 ring-card" />
    ) : null

  const railButtonClass = (active: boolean) =>
    cn(
      'relative flex size-11 touch-manipulation items-center justify-center rounded-lg outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring',
      active ? 'bg-primary text-primary-foreground' : 'text-foreground/70 hover:bg-muted hover:text-foreground',
    )

  const railTooltip = (label: string) => (
    <TooltipContent
      side="right"
      sideOffset={10}
      className="rounded-full border bg-popover px-3 py-1.5 text-xs font-medium text-popover-foreground shadow-lg duration-0 animate-none"
    >
      {label}
    </TooltipContent>
  )

  const railItem = (s: AdminSectionDef) => {
    const Icon = s.icon
    const active = isActiveRoute(s.href)
    return (
      <Tooltip key={s.id} delayDuration={0}>
        <TooltipTrigger asChild>
          <Link
            to={s.href}
            onClick={onNavigate}
            aria-label={hintFor(s)}
            aria-current={active ? 'page' : undefined}
            className={railButtonClass(active)}
          >
            <Icon className="size-[22px]" aria-hidden />
            {railBadge(countFor(s.id), signalFor(s.id), lockedFor(s.id))}
          </Link>
        </TooltipTrigger>
        {railTooltip(hintFor(s))}
      </Tooltip>
    )
  }

  // A group on the rail is one icon; tapping it opens its children beside it.
  const railGroup = (g: GroupNode) => {
    const Icon = GROUP_ICONS[g.id] ?? LayoutGrid
    const holdsCurrent = g.id === currentGroupId
    const count = g.items.reduce((sum, i) => sum + countFor(i.id), 0)
    const signal = strongestSignal(g.items.map((i) => signalFor(i.id)))
    return (
      <Popover key={g.id} open={flyout === g.id} onOpenChange={(o) => setFlyout(o ? g.id : null)}>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button type="button" aria-label={g.label} className={railButtonClass(holdsCurrent)}>
                <Icon className="size-[22px]" aria-hidden />
                {railBadge(count, signal, g.items.every((i) => lockedFor(i.id)))}
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          {flyout !== g.id && railTooltip(g.label)}
        </Tooltip>
        <PopoverContent side="right" align="start" sideOffset={10} className="w-56 p-1.5">
          <p className="px-2.5 pb-1.5 pt-1 text-xs font-medium text-muted-foreground">{g.label}</p>
          <div className="space-y-0.5">{g.items.map(childRow)}</div>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <>
      {isNarrowViewport && showNarrowDrawer && (
        <div
          className="fixed inset-x-0 bottom-0 top-14 z-[50] bg-black/50 md:hidden"
          aria-hidden
          onClick={closeNarrowDrawer}
        />
      )}

      <div
        className={`flex flex-col border-r border-border bg-card transition-[transform,width] duration-300 ease-out ${
          isNarrowViewport
            ? `fixed bottom-0 left-0 top-14 z-[55] max-h-[calc(100dvh-3.5rem)] w-80 max-w-[min(100vw,20rem)] md:hidden ${
                showNarrowDrawer
                  ? 'translate-x-0'
                  : 'pointer-events-none -translate-x-full'
              }`
            : `relative z-30 h-dvh min-h-0 ${desktopCollapsed ? COLLAPSED_RAIL_WIDTH_CLASS : 'w-64'}`
        }`}
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            className={cn(
              'border-b border-border px-3 pb-2 pt-[max(0.75rem,env(safe-area-inset-top,0px))] lg:pt-4',
              stackToggleBelowBrand
                ? 'flex flex-col items-stretch gap-1'
                : 'flex flex-row items-center gap-2'
            )}
          >
            <div
              className={cn(
                'min-w-0',
                stackToggleBelowBrand ? 'w-full' : 'min-w-0 flex-1'
              )}
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Restaurant logo"
                  className={logoImgClass}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                    const parent = e.currentTarget.parentElement
                    if (parent) {
                      parent.innerHTML =
                        '<div class="flex h-12 w-full items-center justify-center bg-primary dark:bg-bhk-ember"><svg class="size-6 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg></div>'
                    }
                  }}
                />
              ) : (
                <div className={fallbackStoreClass}>
                  <Store className={fallbackStoreIconClass} />
                </div>
              )}
            </div>

            <div
              className={cn(
                stackToggleBelowBrand && 'w-full',
                !stackToggleBelowBrand && 'shrink-0'
              )}
            >
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={toggleAriaLabel}
                aria-expanded={sidebarExpanded}
                aria-controls="admin-sidebar-nav"
                onClick={() =>
                  isNarrowViewport ? closeNarrowDrawer() : toggleDesktop()
                }
                className={cn(
                  'touch-manipulation',
                  stackToggleBelowBrand
                    ? 'h-auto min-h-9 w-full shrink-0 py-2.5 text-foreground'
                    : 'h-11 w-11 shrink-0'
                )}
              >
                <ToggleIcon
                  className="size-5"
                  aria-hidden
                />
              </Button>
            </div>
          </div>

          <nav
            id="admin-sidebar-nav"
            aria-label="Admin"
            className={cn(
              'min-h-0 flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]',
              desktopRailCollapsed ? 'flex flex-col items-center gap-1 px-1.5 py-3' : 'space-y-0.5 px-3 py-3',
            )}
          >
            {posEnabled
              ? navTree.map((node) =>
                  node.kind === 'item'
                    ? sidebarFullContent
                      ? topRow(node.item)
                      : railItem(node.item)
                    : sidebarFullContent
                      ? groupBlock(node)
                      : railGroup(node),
                )
              : // Marketing edition: its tools under small static headers.
                navEntries.map((entry) =>
                  'groupHeader' in entry ? (
                    sidebarFullContent ? (
                      <p
                        key={`hdr-${entry.groupHeader}`}
                        className="px-3 pb-1 pt-4 text-xs font-medium text-muted-foreground"
                      >
                        {entry.groupHeader}
                      </p>
                    ) : (
                      <div key={`hdr-${entry.groupHeader}`} className="my-1 w-8 border-t border-border" aria-hidden />
                    )
                  ) : sidebarFullContent ? (
                    topRow(entry)
                  ) : (
                    railItem(entry)
                  ),
                )}
          </nav>

          <div
            className={cn(
              'border-t border-border pb-[max(1rem,env(safe-area-inset-bottom,0px))]',
              desktopRailCollapsed
                ? 'flex justify-center px-2 pt-3'
                : 'p-4'
            )}
          >
            <UserMenu
              user={user}
              collapsed={desktopRailCollapsed}
              size={isTablet ? 'lg' : 'md'}
            />
          </div>
        </div>
      </div>
    </>
  )
}
